import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { TrendingUp, Globe, DollarSign, Hash, ChevronDown, Crown, Cpu } from 'lucide-react'
import { toast } from 'sonner'
import {
  useDomainOverview,
  useRankedKeywords,
  useImportKeywords,
  useTechnologies,
  type DomainOverview,
  type RankedKeyword,
} from '../hooks/useLabs'
import { rankColor } from '@/client/lib/colors'

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(Math.round(n))
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return '$' + (n / 1_000).toFixed(1) + 'K'
  return '$' + n.toFixed(0)
}

function PositionDistribution({ overview }: { overview: DomainOverview }) {
  const o = overview.organic
  const segments = [
    { label: 'Top 3', count: o.pos_1 + o.pos_2_3, color: 'bg-green-500' },
    { label: '4-10', count: o.pos_4_10, color: 'bg-blue-500' },
    { label: '11-20', count: o.pos_11_20, color: 'bg-yellow-500' },
    { label: '21-50', count: o.pos_21_30 + o.pos_31_40 + o.pos_41_50, color: 'bg-orange-400' },
    { label: '51-100', count: o.pos_51_60 + o.pos_61_70 + o.pos_71_80 + o.pos_81_90 + o.pos_91_100, color: 'bg-muted-foreground/30' },
  ]
  const total = segments.reduce((sum, s) => sum + s.count, 0)
  if (total === 0) return null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Position Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex h-6 rounded-full overflow-hidden mb-3">
          {segments.map((seg) => (
            seg.count > 0 && (
              <div
                key={seg.label}
                className={`${seg.color} transition-all`}
                style={{ width: `${(seg.count / total) * 100}%` }}
                title={`${seg.label}: ${formatNumber(seg.count)}`}
              />
            )
          ))}
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          {segments.map((seg) => (
            <div key={seg.label} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-sm ${seg.color}`} />
              <span className="text-muted-foreground">{seg.label}:</span>
              <span className="font-medium">{formatNumber(seg.count)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function rankBadge(position: number) {
  return <span className={`font-medium ${rankColor(position)}`}>{position}</span>
}

function SerpFeatureBadges({ features }: { features: string[] }) {
  if (!features.length) return null
  const displayFeatures = features.slice(0, 3)
  return (
    <div className="flex flex-wrap gap-1">
      {displayFeatures.map((f) => (
        <Badge key={f} variant="outline" className="text-[10px] px-1 py-0">
          {f.replace(/_/g, ' ')}
        </Badge>
      ))}
      {features.length > 3 && (
        <Badge variant="outline" className="text-[10px] px-1 py-0">+{features.length - 3}</Badge>
      )}
    </div>
  )
}

const FILTER_OPTIONS = [
  { label: 'All', value: undefined },
  { label: 'Top 3', value: '3' },
  { label: 'Top 10', value: '10' },
  { label: 'Top 20', value: '20' },
  { label: 'Top 50', value: '50' },
] as const

const SORT_OPTIONS = [
  { label: 'Position', value: 'position,asc' },
  { label: 'Volume', value: 'volume,desc' },
  { label: 'Traffic', value: 'etv,desc' },
] as const

function RankedKeywordsTable({ siteId }: { siteId: string }) {
  const [filter, setFilter] = useState<string | undefined>(undefined)
  const [sort, setSort] = useState('position,asc')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data, isLoading } = useRankedKeywords(siteId, filter, sort, offset)
  const importKeywords = useImportKeywords(siteId)

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const trackedSet = new Set(data?.trackedKeywords ?? [])

  const toggleSelect = (keyword: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(keyword)) next.delete(keyword)
      else next.add(keyword)
      return next
    })
  }

  const handleImport = () => {
    const keywords = Array.from(selected)
    if (!keywords.length) return
    importKeywords.mutate(keywords, {
      onSuccess: (result) => {
        toast.success(`${result.added} keyword${result.added !== 1 ? 's' : ''} added to tracking`)
        setSelected(new Set())
      },
      onError: () => toast.error('Failed to import keywords'),
    })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Ranked Keywords
            {totalCount > 0 && <span className="text-muted-foreground font-normal ml-2">({formatNumber(totalCount)})</span>}
          </CardTitle>
          {selected.size > 0 && (
            <Button size="sm" onClick={handleImport} disabled={importKeywords.isPending}>
              Track Selected ({selected.size})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter + Sort pills */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex flex-wrap gap-1">
            {FILTER_OPTIONS.map((opt) => (
              <Button
                key={opt.label}
                size="sm"
                variant={filter === opt.value ? 'default' : 'outline'}
                className="h-7 text-xs"
                onClick={() => { setFilter(opt.value); setOffset(0); setSelected(new Set()) }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {SORT_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={sort === opt.value ? 'secondary' : 'ghost'}
                className="h-7 text-xs"
                onClick={() => { setSort(opt.value); setOffset(0) }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No ranked keywords found for this domain.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="w-8 py-2" />
                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Keyword</th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">Pos</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground w-20">Volume</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground w-16">CPC</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground w-16">Traffic</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden lg:table-cell">URL</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden xl:table-cell">SERP</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: RankedKeyword) => {
                    const isTracked = trackedSet.has(item.keyword.toLowerCase())
                    return (
                      <tr key={item.keyword} className={`border-b last:border-0 ${isTracked ? 'opacity-50' : ''}`}>
                        <td className="py-1.5 text-center">
                          {isTracked ? (
                            <Badge variant="outline" className="text-[10px] px-1">✓</Badge>
                          ) : (
                            <Checkbox
                              checked={selected.has(item.keyword)}
                              onCheckedChange={() => toggleSelect(item.keyword)}
                            />
                          )}
                        </td>
                        <td className="py-1.5 pr-4">
                          <span className="truncate block max-w-[300px]" title={item.keyword}>{item.keyword}</span>
                        </td>
                        <td className="text-center py-1.5 px-2">{rankBadge(item.position)}</td>
                        <td className="text-right py-1.5 px-2 text-muted-foreground">
                          {item.search_volume != null ? formatNumber(item.search_volume) : '-'}
                        </td>
                        <td className="text-right py-1.5 px-2 text-muted-foreground">
                          {item.cpc != null ? `$${item.cpc.toFixed(2)}` : '-'}
                        </td>
                        <td className="text-right py-1.5 px-2 text-muted-foreground">
                          {item.etv != null ? formatNumber(item.etv) : '-'}
                        </td>
                        <td className="py-1.5 px-2 hidden lg:table-cell">
                          {item.url && (
                            <span className="text-xs text-muted-foreground truncate block max-w-[200px]" title={item.url}>
                              {new URL(item.url).pathname}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 px-2 hidden xl:table-cell">
                          <SerpFeatureBadges features={item.serp_features} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t">
              <span className="text-xs text-muted-foreground">
                Showing {offset + 1}-{Math.min(offset + items.length, totalCount)} of {formatNumber(totalCount)}
              </span>
              <div className="flex gap-2">
                {offset > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setOffset(Math.max(0, offset - 100))}>
                    Previous
                  </Button>
                )}
                {offset + items.length < totalCount && (
                  <Button size="sm" variant="outline" onClick={() => setOffset(offset + 100)}>
                    <ChevronDown className="mr-1 h-3 w-3 -rotate-90" />
                    Load More
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Category display labels and order
const CATEGORY_ORDER: Record<string, number> = {
  'cms': 0,
  'analytics': 1,
  'javascript_frameworks': 2,
  'javascript_libraries': 3,
  'cdn': 4,
  'security': 5,
  'seo': 6,
  'cookie_compliance': 7,
  'hosting': 8,
  'web_servers': 9,
  'miscellaneous': 10,
}

function categoryLabel(category: string): string {
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const CATEGORY_COLORS: Record<string, string> = {
  cms: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  analytics: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cdn: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  security: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  seo: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
}

function TechStackSection({ siteId }: { siteId: string }) {
  const { data, isLoading } = useTechnologies(siteId)

  if (isLoading) return <Skeleton className="h-32 w-full" />

  const technologies = data?.technologies ?? []
  if (!technologies.length) return null

  // Group by category
  const grouped = new Map<string, string[]>()
  for (const tech of technologies) {
    const existing = grouped.get(tech.category) ?? []
    existing.push(tech.name)
    grouped.set(tech.category, existing)
  }

  // Sort categories
  const sortedCategories = Array.from(grouped.entries()).sort(
    ([a], [b]) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99)
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Cpu className="h-4 w-4" />
          Tech Stack
          <span className="text-muted-foreground font-normal text-sm">({technologies.length} detected)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sortedCategories.map(([category, names]) => (
          <div key={category}>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">{categoryLabel(category)}</p>
            <div className="flex flex-wrap gap-1.5">
              {names.map((name) => (
                <span
                  key={name}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                    CATEGORY_COLORS[category] ?? 'bg-muted text-foreground'
                  }`}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function ManagedTierGate() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Crown className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
        <h3 className="text-lg font-semibold mb-1">Managed Tier Required</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Domain intelligence features are available for managed sites. Enable managed tier in the Schedule tab to access keyword discovery, domain overview, and competitor analysis.
        </p>
      </CardContent>
    </Card>
  )
}

export function DomainIntelTab({ siteId, tier }: { siteId: string; tier: string }) {
  if (tier !== 'managed') return <ManagedTierGate />

  return (
    <div className="space-y-6">
      <OverviewCards siteId={siteId} />
      <RankedKeywordsTable siteId={siteId} />
      <TechStackSection siteId={siteId} />
    </div>
  )
}

function OverviewCards({ siteId }: { siteId: string }) {
  const { data, isLoading } = useDomainOverview(siteId)

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    )
  }

  const overview = data?.overview
  if (!overview) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-sm text-muted-foreground">
            No domain data available. This domain may not have enough organic rankings for DataForSEO to track.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <TrendingUp className="h-3.5 w-3.5" />
              Est. Monthly Traffic
            </div>
            <p className="text-2xl font-bold">{formatNumber(overview.organic.etv)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" />
              Traffic Value
            </div>
            <p className="text-2xl font-bold">{formatCurrency(overview.organic.estimated_paid_traffic_cost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Hash className="h-3.5 w-3.5" />
              Organic Keywords
            </div>
            <p className="text-2xl font-bold">{formatNumber(overview.organic.count)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Globe className="h-3.5 w-3.5" />
              Paid Keywords
            </div>
            <p className="text-2xl font-bold">{formatNumber(overview.paid.count)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" />
              Paid Traffic Value
            </div>
            <p className="text-2xl font-bold">{formatCurrency(overview.paid.etv)}</p>
          </CardContent>
        </Card>
      </div>
      <PositionDistribution overview={overview} />
    </>
  )
}
